import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faUser } from '@fortawesome/free-solid-svg-icons';
import CommonTable from '../../../../common/table/ComonTable';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';
import apiServices from '../../../../../services/apiService';
import { exportToExcel } from '../../../../../utils/excelUtils';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import SectionTab from '../../../../common/sectiontab/SectionTab';

const VisitorTableTab = ({ visitorTypeId, tableHeading }) => {
    const { user } = useSelector((state) => state.auth);
    const [currentPage, setCurrentPage] = useState(1);
    const [allVisitors, setAllVisitors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageInfo, setPageInfo] = useState({ pageSize: 10 });
    const [totalRecords, setTotalRecords] = useState(0);

    const [statusFilter, setStatusFilter] = useState("All");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [searchText, setSearchText] = useState("");

    const fetchVisitorsData = async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams();
            
            let decodedToken = {};
            const token = localStorage.getItem("token");
            if (token) {
                try {
                    decodedToken = jwtDecode(token);
                } catch (e) {
                    console.error("Error decoding token", e);
                }
            }

            const siteId = decodedToken?.siteId || user?.siteId;
            const unitId = decodedToken?.branchorunitID || decodedToken?.unitId || user?.branchorunitID || user?.unitId;
            
            const rawRoleId = decodedToken?.roleId || user?.roleId || user?.RoleId || localStorage.getItem("roleId");
            const roleId = rawRoleId ? Number(rawRoleId) : null;

            if (siteId) queryParams.append("siteId", siteId);
            
            // If the user is a unitAdmin (roleId 4), append unitId
            if (roleId === 4 && unitId) {
                queryParams.append("unitId", unitId);
            }
            
            queryParams.append("pageNumber", currentPage);
            queryParams.append("pageSize", pageInfo.pageSize);
            
            if (visitorTypeId) {
                queryParams.append("visitorTypeId", visitorTypeId);
            }
            if (startDate) {
                queryParams.append("startDate", new Date(startDate).toISOString());
            }
            if (endDate) {
                queryParams.append("endDate", new Date(endDate).toISOString());
            }
            if (searchText) {
                queryParams.append("searchText", searchText);
            }

            const response = await apiServices({
                method: "GET",
                url: `/Visitor/getAllVisitor?${queryParams.toString()}`,
            });
            if (response?.success) {
                setAllVisitors(response.data?.data || []);
                setTotalRecords(response.data?.totalRecords || response.data?.totalCount || response.data?.total || response.data?.data?.length || 0);
            }
        } catch (error) {
            console.error("Error fetching visitors:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.siteId) {
            fetchVisitorsData();
        }
    }, [currentPage, pageInfo.pageSize, startDate, endDate, user?.siteId, user?.branchorunitID, user?.unitId, user?.roleId, user?.RoleId, visitorTypeId, searchText]);

    const filteredVisitors = useMemo(() => {
        let result = allVisitors || [];
        if (statusFilter !== "All") {
            result = result.filter(v => v.statusName === statusFilter || v.status === statusFilter);
        }
        return result;
    }, [allVisitors, statusFilter]);

    const paginatedVisitors = filteredVisitors;
    const totalPages = Math.ceil(totalRecords / pageInfo.pageSize) || 1;

    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, pageInfo.pageSize, startDate, endDate]);

    const formatTime = (dateString) => {
        if (!dateString || dateString === "—") return "—";
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;

            const day = date.getDate().toString().padStart(2, '0');
            const month = date.toLocaleString('en-US', { month: 'short' });
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        } catch (e) {
            return dateString;
        }
    };

    const handleExport = () => {
        const excelHeaders = [
            { label: "NO", key: "no" },
            { label: "VISITOR NAME", key: "name" },
            { label: "SITE NAME", key: "siteName" },
            { label: "UNIT", key: "unitName" },
            { label: "MOBILE", key: "mobileNumber" },
            { label: "PURPOSE", key: "purposeOfVisit" },
            { label: "DATE", key: "createdDate" },
            { label: "STATUS", key: "statusName" }
        ];

        const exportData = filteredVisitors.map((v, i) => ({
            no: i + 1,
            siteName: v.siteName,
            unitName: v.unitName,
            name: v.name || v.visitorName,
            mobileNumber: v.mobileNumber || v.visitorMobileNumber,
            purposeOfVisit: v.purposeOfVisit,
            createdDate: formatTime(v.expectedCheckIn || v.createdDate),
            statusName: v.statusName || v.status
        }));

        exportToExcel(exportData, excelHeaders, `Visitor_Records_${visitorTypeId === 1 ? 'Invited' : 'Walkin'}`);
    };

    const tableHeaders = [
        { label: "NO", key: "no" },
        { label: "VISITOR", key: "visitorInfo" },
        // { label: "SITE ", key: "siteName" },
        { label: "UNIT", key: "unitName" },
        { label: "MOBILE", key: "visitorMobileNumber" },
        // { label: "LOCATION", key: "location" },
        { label: "PURPOSE", key: "purposeOfVisit" },
        { label: "DATE", key: "createdDate" },
        { label: "STATUS", key: "status" },
    ];

    const renderCell = (key, row) => {
        switch (key) {
            case "no":
                return <span className="text-xs font-normal text-[#000000] dark:text-textsecondary">{row.no}</span>;
            case "visitorInfo":
                return (
                    <div className="flex items-center gap-3">
                        {row.image ? (
                            <img
                                src={`${row.image}`}
                                className="w-8 h-8 rounded-full border border-gray-100 dark:border-bordercolor object-cover"
                                alt="Visitor"
                            />
                        ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-actionitembg border border-gray-200 dark:border-bordercolor flex items-center justify-center text-gray-400 dark:text-textsecondary">
                                <FontAwesomeIcon icon={faUser} className="text-xs" />
                            </div>
                        )}
                        <span className="text-xs font-bold text-[#000000] dark:text-textprimary">{row.visitorName || row.name || "—"}</span>
                    </div>
                );
            case "siteName":
            case "unitName":
            case "visitorMobileNumber":
            case "location":
            case "purposeOfVisit":
                return <span className="text-xs font-normal text-[#64748B] dark:text-textsecondary">{row[key] ?? "—"}</span>;
            case "createdDate":
                return <span className="text-xs font-normal text-[#64748B] dark:text-textsecondary">{formatTime(row.expectedCheckIn || row.createdDate)}</span>;
            case "status":
                let statusColor = "bg-gray-100 text-gray-700 dark:bg-actionitembg dark:text-textsecondary";
                const statusVal = row.statusName || row.status || "";
                const normalizedStatus = statusVal.toUpperCase().replace(/[-_ ]/g, "");

                if (normalizedStatus === "CHECKEDIN") {
                    statusColor = "bg-[#004B591A] text-[#004B59]";
                } else if (normalizedStatus === "CHECKEDOUT" || normalizedStatus === "CHECKOUT") {
                    statusColor = "bg-[#E31F262B] text-[#E31F26]";
                } else if (normalizedStatus === "PENDING") {
                    statusColor = "bg-[#FEF9C3] text-[#D97706]";
                } else if (normalizedStatus === "APPROVED") {
                    statusColor = "bg-[#DCFCE7] text-[#166534]";
                } else if (normalizedStatus === "REJECTED") {
                    statusColor = "bg-[#E31F26] text-[#FFFFFF]";
                } else if (normalizedStatus === "BLOCKED" || normalizedStatus === "BLOCK") {
                    statusColor = "bg-[#0F172A] text-white border border-[#0F172A]";
                }

                return (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black shadow-sm uppercase ${statusColor}`}>
                        {statusVal}
                    </span>
                );
            default:
                return <span className="text-xs font-normal text-[#000000] dark:text-textprimary">{row[key] || "—"}</span>;
        }
    };

    const headerAction = (
        <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex items-center w-48 sm:w-64">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                </span>
                <input
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search visitors..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-bordercolor rounded-lg text-sm focus:outline-none bg-white dark:bg-actionitembg text-textprimary hover:border-gray-400"
                />
            </div>
            <div className="flex items-center gap-2">
                <input 
                    type="date" 
                    value={startDate} 
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-2 bg-white dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-lg text-xs font-bold text-[#0F172A] dark:text-textprimary focus:outline-none focus:ring-1 focus:border-gray-400 cursor-pointer"
                />
                <span className="text-xs font-bold text-[#64748B] dark:text-textsecondary">to</span>
                <input 
                    type="date" 
                    value={endDate} 
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-2 bg-white dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-lg text-xs font-bold text-[#0F172A] dark:text-textprimary focus:outline-none focus:ring-1 focus:border-gray-400 cursor-pointer"
                />
            </div>
            <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 bg-white dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-lg text-xs font-bold text-[#0F172A] dark:text-textprimary focus:outline-none focus:ring-1 focus:border-gray-400"
            >
                <option value="All">All Status</option>
                <option value="Approved">Approved</option>
                <option value="Pending">Pending</option>
                <option value="Rejected">Rejected</option>
            </select>
            <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 bg-[#F8FAFC] dark:bg-actionitembg border border-[#E2E8F0] dark:border-bordercolor text-[#64748B] dark:text-textsecondary text-xs font-bold rounded-lg hover:bg-white dark:hover:bg-cardbg hover:text-[#1E293B] dark:hover:text-textprimary hover:border-[#1E293B] dark:hover:border-gray-600 transition-all duration-200 shadow-sm"
            >
                <FontAwesomeIcon icon={faDownload} className=" text-sm" />
                <span>Export</span>
            </button>
        </div>
    );

    const mappedVisitorsData = paginatedVisitors.map((v, index) => ({
        ...v,
        no: ((currentPage - 1) * pageInfo.pageSize) + index + 1,
    }));

    return (
        <div className="shrink-0 flex-1 flex flex-col bg-white dark:bg-sectionheaderbg rounded-xl shadow-sm border border-gray-100 dark:border-bordercolor overflow-hidden">
            <CommonTable
                tableHeading={tableHeading}
                data={mappedVisitorsData}
                headers={tableHeaders}
                renderCell={renderCell}
                headerAction={headerAction}
                pagination={{
                    pageSize: pageInfo.pageSize,
                    pageNumber: currentPage,
                    totalPages: totalPages,
                    totalRecords: totalRecords
                }}
                loading={loading}
                onPageChange={setCurrentPage}
                onLimitChange={(newLimit) => setPageInfo(prev => ({ ...prev, pageSize: newLimit }))}
                showActionCol={false}
            />
        </div>
    );
};

const InvitedVisitorTab = () => <VisitorTableTab visitorTypeId={1} tableHeading="Invited Visitors Reports" />;
const WalkinVisitorTab = () => <VisitorTableTab visitorTypeId={2} tableHeading="Walk-in Visitor Reports" />;

const VisitorReports = () => {
    const tabs = [
        { key: "invited", label: "Invited Visitor", component: InvitedVisitorTab },
        { key: "walkin", label: "Walk-in Visitor", component: WalkinVisitorTab }
    ];

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto gap-4 pt-4 scrollbar-hide">
            <div className='flex flex-col gap-1 shrink-0'>
                <Breadcrumbs />
                <PageHeadingSection
                    mainHeading="Visitor Reports"
                    subHeading="Detailed log of all visitor records."
                />
            </div>

            <SectionTab tabs={tabs} defaultTab="invited" />
        </div>
    );
};

export default VisitorReports;
